import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { ArrowRight, MapPin } from 'lucide-react';
import { indonesiaTours } from '../data/tours';
import { indonesiaGeographicGroups } from '../data/mock';

export const IndonesiaGeographicGroupPage = () => {
  const { groupId } = useParams();
  const group = indonesiaGeographicGroups.find(g => g.id === groupId);

  if (!group) {
    return (
      <div>
        <Navigation />
        <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>
          <h1 className="hero-large">Region Not Found</h1>
          <Link to="/indonesia-journeys" style={{ color: 'var(--gold)', textDecoration: 'none' }}>
            ← Back to Indonesia Journeys
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Filter tours for this geographic group
  const groupTours = indonesiaTours.filter(tour => tour.geographicGroup === groupId);

  return (
    <div>
      <Navigation />
      <WhatsAppButton variant="floating" />

      {/* Hero Section */}
      <section style={{
        background: 'var(--bg-secondary)',
        padding: '120px 0 80px',
        textAlign: 'center'
      }}>
        <div className="container">
          <div style={{ marginBottom: '24px' }}>
            <Link 
              to="/indonesia-journeys"
              style={{ 
                color: 'var(--text-secondary)', 
                textDecoration: 'none',
                fontSize: '14px',
                transition: 'color 0.3s ease'
              }}
              className="back-link"
            >
              ← Back to Indonesia Journeys
            </Link>
          </div>
          <h1 className="hero-large" style={{ marginBottom: '16px' }}>
            {group.name}
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
            {group.description}
          </p>
        </div>
      </section>

      {/* Tours Grid */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          {groupTours.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                No journeys currently available for this region. Please check back soon.
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '40px'
            }}>
              {groupTours.map((tour) => (
                <Link
                  key={tour.id}
                  to={`/tours/${tour.id}`}
                  style={{
                    textDecoration: 'none',
                    display: 'block',
                    borderRadius: 0,
                    overflow: 'hidden',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-light)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                  }}
                  className="tour-card"
                >
                  <div style={{
                    width: '100%',
                    height: '280px',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={tour.image}
                      alt={tour.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      className="tour-card-image"
                    />
                  </div>
                  <div style={{ padding: '32px' }}>
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      marginBottom: '16px',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{
                        fontSize: '12px',
                        padding: '4px 12px',
                        background: 'var(--bg-secondary)',
                        color: 'var(--text-secondary)'
                      }}>
                        {tour.duration}
                      </span>
                    </div>
                    <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                      {tour.title}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                      {tour.subtitle}
                    </p>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: 'var(--text-secondary)',
                      marginBottom: '20px'
                    }}>
                      <MapPin size={16} />
                      <span className="body-small">{tour.destination}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: 'var(--interactive-base)',
                      fontWeight: 500
                    }}>
                      <span>View Details</span>
                      <ArrowRight size={18} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      <style>{`
        .tour-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.1);
        }
        .tour-card:hover .tour-card-image {
          transform: scale(1.05);
        }
      `}</style>
    </div>
  );
};
